import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import type { AbstractAgent } from '@ag-ui/client';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useAgUiRuntime } from '@assistant-ui/react-ag-ui';

import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { Offer } from '@makemydeal/dr-platform-types';

import { createDealAssistantAgent } from '../agent/createDealAssistantAgent';
import type { CreateDealAssistantAgentParams } from '../agent/createDealAssistantAgent';
import { trackOpenPanel, trackSendFailed, trackSnapshotFailed } from '../monitoring/dealAssistantMonitoring';
import type { DealAssistantFailureDetails } from '../monitoring/dealAssistantMonitoring';
import { DealAssistantStateBridge } from './DealAssistantStateBridge';
import { toFailureDetails } from './dealAssistantFailure';

export type DealAssistantProviderProps = {
    children: ReactNode;
    getOfferSnapshot: () => Offer;
    getDealStructureSnapshotLabel?: () => string | undefined;
    createAgent?: (params: CreateDealAssistantAgentParams) => AbstractAgent;
};

export const DealAssistantProvider = ({
    children,
    getOfferSnapshot,
    getDealStructureSnapshotLabel,
    createAgent = createDealAssistantAgent
}: DealAssistantProviderProps) => {
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealerIdRef = useRef(dealerId);
    dealerIdRef.current = dealerId;

    const [agent] = useState(() => createAgent({ getDealerId: () => dealerIdRef.current }));
    const [isOpen, setIsOpen] = useState(false);
    const [offerSnapshot, setOfferSnapshot] = useState<Offer | undefined>(undefined);
    const [failure, setFailure] = useState<DealAssistantFailureDetails | undefined>(undefined);
    const snapshotTakenRef = useRef(false);

    const runtime = useAgUiRuntime({
        agent,
        onError: (error) => {
            const details = toFailureDetails(error);
            setFailure(details);
            trackSendFailed(details);
        }
    });

    const open = useCallback(() => {
        if (!snapshotTakenRef.current) {
            try {
                setOfferSnapshot(getOfferSnapshot());
                snapshotTakenRef.current = true;
            } catch (error) {
                trackSnapshotFailed(error);
            }
        }
        setIsOpen(true);
        trackOpenPanel();
    }, [getOfferSnapshot]);

    const close = useCallback(() => setIsOpen(false), []);
    const clearFailure = useCallback(() => setFailure(undefined), []);

    return (
        <AssistantRuntimeProvider runtime={runtime}>
            <DealAssistantStateBridge
                runtime={runtime}
                agent={agent}
                isOpen={isOpen}
                offerSnapshot={offerSnapshot}
                failure={failure}
                open={open}
                close={close}
                clearFailure={clearFailure}
                getDealStructureSnapshotLabel={getDealStructureSnapshotLabel}
            >
                {children}
            </DealAssistantStateBridge>
        </AssistantRuntimeProvider>
    );
};
